import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FREE_NOTE_LIMIT } from "@caliu/types";
import { loadProOffering } from "../lib/purchases";
import { track, AnalyticsEvent } from "../lib/analytics";
import { useUIStore } from "../store/ui";
import { useEffectiveNoteLimit } from "../hooks/useNoteLimit";
import {
  ProFeatureListBox,
  ProWebFootnote,
  ProLockup,
  ProPlanPurchase,
} from "./PaywallShared";
import { Dialog, DialogCloseButton, DialogContent } from "./ui/dialog";

/**
 * Caliu's own Pro paywall (mirrors the native `PaywallSheet`). RevenueCat Web
 * Billing supplies the offering (packages + localized prices) and runs checkout;
 * we render the UI. With no live offering it shows the static price plus the
 * way forward: sign-in in local mode (Pro binds to a Caliu account), a retry
 * otherwise. Grandfathered / cross-platform subscribers are already unlocked
 * server-side and never see this.
 *
 * The lockup, feature box, plan cards + purchase all
 * come from `PaywallShared`, so this dialog and the onboarding Pro step render
 * the exact same surface. Only the header copy (subtitle vs onboarding's
 * title/subtitle) differs.
 *
 * Mounted once at the app root; driven by `useUIStore.paywallOpen`.
 */
export function PaywallDialog() {
  const { t } = useTranslation();
  const open = useUIStore((s) => s.paywallOpen);
  const closePaywall = useUIStore((s) => s.closePaywall);
  const paywallFeature = useUIStore((s) => s.paywallFeature);
  const noteLimit = useEffectiveNoteLimit();
  // RevenueCat mounts its checkout as a new node on `<body>`, outside this
  // dialog. While a Radix modal is open, everything outside it is inert
  // (`pointer-events: none` on body, focus trap, scroll lock), so the checkout
  // rendered but swallowed every tap: no web checkout could ever complete.
  // Step aside while it is up; a cancel brings the paywall back.
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  // Warm the offering cache the moment the dialog opens so `ProPlanPurchase`
  // (mounted with the DialogContent) resolves its prices instantly.
  useEffect(() => {
    if (open) void loadProOffering();
  }, [open]);

  // User-initiated close (X, escape, outside click) — closing after a purchase
  // goes through the raw `closePaywall` (via `onPurchased`), which never lands
  // here, so the funnel doesn't count a conversion as a dismissal.
  const dismiss = () => {
    track(AnalyticsEvent.PaywallDismissed, { feature: paywallFeature });
    closePaywall();
  };

  return (
    <Dialog open={open && !checkoutOpen} onOpenChange={(o) => !o && dismiss()}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[460px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden border-none p-0">
        {/* accent glow fading into the canvas (mirrors the native paywall's
            LinearGradient: accent@16% → bg by ~45% of the height) */}
        <div
          aria-hidden
          className="pro-accent-glow pointer-events-none absolute inset-0"
        />

        <div className="absolute right-4 top-4 z-10">
          <DialogCloseButton onClick={dismiss} />
        </div>

        <div className="relative min-h-0 flex-1 overflow-y-auto px-5 pb-7 pt-9 sm:px-9">
          {/* header */}
          <div className="flex flex-col items-center text-center">
            <ProLockup />
            {paywallFeature === "note_limit" ? (
              // The free note limit: say what happened and what keeps working
              // (editing, search, sync) before the plans.
              <>
                <h2 className="mt-3 text-base font-semibold text-ink">
                  {t("noteLimit.paywallTitle", { limit: noteLimit ?? FREE_NOTE_LIMIT })}
                </h2>
                <p className="mt-1.5 text-sm text-muted">
                  {t("noteLimit.paywallBody")}
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm text-muted">{t("pro.subtitle")}</p>
            )}
          </div>

          <div className="mt-7">
            <ProFeatureListBox footnote={false} />
          </div>

          {/* Same gap as between the plan tiles, so every card sits evenly. */}
          <div className="mt-2.5 sm:mt-3.5">
            <ProPlanPurchase
              onPurchased={closePaywall}
              onCheckoutChange={setCheckoutOpen}
            />
          </div>

          <div className="mt-6">
            <ProWebFootnote />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
